<template>
    <div class="profile-wrapper">
        <div class="profile-container">
            <header class="profile-page-header">
                <div>
                    <p class="profile-eyebrow">Paramètres</p>
                    <h1>Profil et utilisateurs</h1>
                </div>
                <div v-if="isAdmin" class="settings-overview">
                    <div class="overview-tile">
                        <span class="overview-label">Utilisateurs</span>
                        <strong>{{ safeUsers.length }}</strong>
                    </div>
                    <div class="overview-tile">
                        <span class="overview-label">Postes</span>
                        <strong>{{ safePosts.length }}</strong>
                    </div>
                </div>
            </header>

            <div class="settings-grid">
                <div class="settings-main">
                    <section class="profile-card">
                        <div class="card-header">
                            <div>
                                <p class="card-kicker">Compte actuel</p>
                                <h3>Changer mon mot de passe</h3>
                            </div>
                        </div>

                        <form @submit.prevent="submitProfileForm" class="form">
                            <div class="form-section">
                                <div class="form-row">
                                    <div class="form-group">
                                        <label for="currentPassword" class="form-label">Mot de passe actuel</label>
                                        <input v-model="profileForm.current_password" type="password"
                                            id="currentPassword" class="form-input" placeholder="••••••••" />
                                    </div>
                                    <div class="form-group">
                                        <label for="newPasswordProfile" class="form-label">Nouveau mot de passe</label>
                                        <input v-model="profileForm.new_password" type="password"
                                            id="newPasswordProfile" class="form-input" placeholder="••••••••" />
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label for="confirmPassword" class="form-label">Confirmer le mot de passe</label>
                                    <input v-model="profileForm.confirm_password" type="password" id="confirmPassword"
                                        class="form-input" placeholder="••••••••" />
                                </div>
                            </div>

                            <div class="form-actions">
                                <button type="submit" :disabled="profileLoading" class="btn btn-primary">
                                    <span v-if="!profileLoading">Mettre à jour</span>
                                    <span v-else>Mise à jour...</span>
                                </button>
                            </div>

                            <div v-if="profileSuccess" class="alert alert-success">
                                Profil mis à jour avec succès.
                            </div>
                            <div v-if="profileError" class="alert alert-error">
                                {{ profileError }}
                            </div>
                        </form>
                    </section>

                    <section v-if="isAdmin" class="profile-card">
                        <div class="card-header">
                            <div>
                                <p class="card-kicker">Planning RH</p>
                                <h3>Journée restaurant</h3>
                            </div>
                        </div>

                        <form @submit.prevent="saveSettings" class="form">
                            <div class="form-section">
                                <div class="form-group">
                                    <label for="rhClosingTime" class="form-label">Début de journée</label>
                                    <input v-model="settingsForm.rh_closing_time" id="rhClosingTime" type="time"
                                        class="form-input" required />
                                    <p class="form-help">
                                        Avant cette heure, une sortie ferme la journée précédente. Une nouvelle entrée est refusée.
                                    </p>
                                </div>
                            </div>

                            <div class="form-actions">
                                <button type="submit" :disabled="settingsLoading" class="btn btn-primary">
                                    <span v-if="!settingsLoading">Enregistrer</span>
                                    <span v-else>Enregistrement...</span>
                                </button>
                            </div>

                            <div v-if="settingsSuccess" class="alert alert-success">
                                Paramètres RH enregistrés.
                            </div>
                            <div v-if="settingsError" class="alert alert-error">
                                {{ settingsError }}
                            </div>
                        </form>
                    </section>

                    <section v-if="isAdmin" class="profile-card">
                        <div class="card-header">
                            <div>
                                <p class="card-kicker">Accès admin</p>
                                <h3>Créer un caissier ou admin</h3>
                            </div>
                        </div>

                        <form @submit.prevent="submitCreateUserForm" class="form">
                            <div class="form-section">
                                <div class="form-row form-row-three">
                                    <div class="form-group">
                                        <label for="newUserRole" class="form-label">Rôle</label>
                                        <select v-model="createUserForm.role" id="newUserRole" class="form-input">
                                            <option value="caissier">Caissier</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>
                                    <div class="form-group">
                                        <label for="newUsername" class="form-label">Nom d'utilisateur</label>
                                        <input v-model="createUserForm.username" type="text" id="newUsername"
                                            class="form-input" required />
                                    </div>
                                    <div class="form-group">
                                        <label for="newPassword" class="form-label">Mot de passe</label>
                                        <input v-model="createUserForm.password" type="password" id="newPassword"
                                            class="form-input" placeholder="••••••••" required />
                                    </div>
                                    <div class="form-group">
                                        <label for="newPasswordConfirm" class="form-label">Confirmation</label>
                                        <input v-model="createUserForm.passwordconf" type="password"
                                            id="newPasswordConfirm" class="form-input" placeholder="••••••••"
                                            required />
                                    </div>
                                </div>
                            </div>

                            <div class="form-actions">
                                <button type="button" @click="resetCreateUserForm" class="btn btn-secondary">
                                    Réinitialiser
                                </button>
                                <button type="submit" :disabled="createUserLoading" class="btn btn-primary">
                                    <span v-if="!createUserLoading">Créer l'utilisateur</span>
                                    <span v-else>Création...</span>
                                </button>
                            </div>

                            <div v-if="createUserSuccess" class="alert alert-success">
                                Utilisateur créé avec succès.
                            </div>
                            <div v-if="createUserError" class="alert alert-error">
                                {{ createUserError }}
                            </div>
                        </form>
                    </section>

                    <section v-if="isAdmin" class="profile-card">
                        <div class="card-header">
                            <div>
                                <p class="card-kicker">Comptes employés</p>
                                <h3>Créer et affecter en masse</h3>
                            </div>
                        </div>

                        <form @submit.prevent="submitBulkEmployeeUsers" class="form">
                            <div class="form-section">
                                <div class="form-row">
                                    <div class="form-group">
                                        <label class="form-label">Caissier responsable</label>
                                        <select v-model.number="bulkEmployeeForm.cashier_user_id" class="form-input" required>
                                            <option :value="null">Choisir un caissier</option>
                                            <option v-for="user in cashierUsers" :key="user.id" :value="user.id">
                                                {{ user.username }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="bulk-summary">
                                        <strong>{{ selectedBulkRows.length }}</strong>
                                        <span>employé(s) prêt(s)</span>
                                    </div>
                                </div>

                                <div v-if="!bulkEmployeeForm.cashier_user_id" class="empty-inline">
                                    Choisissez un caissier pour afficher les employés disponibles.
                                </div>
                                <div v-else class="bulk-panel">
                                    <div v-if="bulkEmployeeRows.length === 0" class="empty-inline">
                                        Aucun employé sans compte utilisateur
                                    </div>
                                    <div v-for="row in bulkEmployeeRows" :key="row.staff_id" class="bulk-row"
                                        :class="{ selected: row.selected }">
                                        <label class="bulk-employee-line">
                                            <input type="checkbox" v-model="row.selected" @change="prepareBulkRow(row)" />
                                            <span>
                                                <strong>{{ staffName(row.staff) }}</strong>
                                                <small>{{ row.staff.post_name || 'Sans poste' }} · {{ row.staff.group_name || 'Sans groupe' }}</small>
                                            </span>
                                            <b v-if="row.selected">Compte à créer</b>
                                        </label>
                                        <div v-if="row.selected" class="bulk-fields">
                                            <div class="form-group">
                                                <label class="form-label">Nom d'utilisateur</label>
                                                <input v-model.trim="row.username" type="text" class="form-input" />
                                            </div>
                                            <div class="form-group">
                                                <label class="form-label">Mot de passe</label>
                                                <input v-model="row.password" type="password" class="form-input"
                                                    placeholder="••••••••" />
                                            </div>
                                            <div class="form-group">
                                                <label class="form-label">Confirmation</label>
                                                <input v-model="row.passwordconf" type="password" class="form-input"
                                                    placeholder="••••••••" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                                <div class="form-actions">
                                    <button type="submit" :disabled="bulkEmployeeLoading || selectedBulkRows.length === 0" class="btn btn-primary">
                                        <span v-if="!bulkEmployeeLoading">Créer les comptes employés</span>
                                        <span v-else>Création...</span>
                                </button>
                            </div>

                            <div v-if="bulkEmployeeSuccess" class="alert alert-success">
                                {{ bulkEmployeeSuccess }}
                            </div>
                            <div v-if="bulkEmployeeError" class="alert alert-error">
                                {{ bulkEmployeeError }}
                            </div>
                        </form>
                    </section>

                </div>

                <aside v-if="isAdmin" class="settings-side">
                    <section class="profile-card organization-card">
                        <div class="card-header">
                            <div>
                                <p class="card-kicker">Organisation</p>
                                <h3>Postes</h3>
                            </div>
                        </div>

                        <div class="organization-body">
                            <form class="post-create-form" @submit.prevent="addPost">
                                <div class="form-group">
                                    <label for="postName" class="form-label">Nom du poste</label>
                                    <input v-model="postForm.name" id="postName" type="text" class="form-input"
                                        placeholder="Ex: Cuisine" />
                                </div>
                                <button type="submit" class="icon-command primary" :disabled="postLoading"
                                    :title="postLoading ? 'Création...' : 'Créer le poste'">
                                    <svg v-if="!postLoading" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2.5">
                                        <path d="M12 5v14M5 12h14" />
                                    </svg>
                                    <span v-else class="mini-spinner"></span>
                                </button>
                            </form>
                            <div v-if="postSuccess" class="alert alert-success">{{ postSuccess }}</div>
                            <div v-if="postError" class="alert alert-error">{{ postError }}</div>
                            <div class="posts-manager-list">
                                <div v-for="post in editablePosts" :key="post.originalName" class="post-config-row">
                                    <div class="post-config-main">
                                        <input v-model="post.name" type="text" class="form-input post-name-input" />
                                    </div>
                                    <div class="row-command-group">
                                        <button type="button" class="icon-command"
                                            :disabled="post.saving"
                                            @click="updatePost(post)"
                                            :title="post.saving ? 'Enregistrement...' : 'Enregistrer'">
                                            <svg v-if="!post.saving" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                stroke-width="2.5">
                                                <path d="M20 6 9 17l-5-5" />
                                            </svg>
                                            <span v-else class="mini-spinner"></span>
                                        </button>
                                        <button type="button" class="icon-command danger"
                                            :disabled="post.deleting"
                                            @click="deletePost(post)"
                                            :title="post.deleting ? 'Suppression...' : 'Supprimer'">
                                            <svg v-if="!post.deleting" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M3 6h18" />
                                                <path d="M8 6V4h8v2" />
                                                <path d="M19 6l-1 14H6L5 6" />
                                                <path d="M10 11v5M14 11v5" />
                                            </svg>
                                            <span v-else class="mini-spinner"></span>
                                        </button>
                                    </div>
                                </div>
                                <span v-if="safePosts.length === 0" class="empty-inline">Aucun poste créé</span>
                            </div>
                        </div>
                    </section>

                    <section class="profile-card organization-card">
                        <div class="card-header">
                            <div>
                                <p class="card-kicker">Horaires</p>
                                <h3>Groupes horaires</h3>
                            </div>
                        </div>

                        <div class="organization-body">
                            <form class="group-create-form" @submit.prevent="addGroup">
                                <div class="form-group">
                                    <label class="form-label">Nom du groupe</label>
                                    <input v-model="groupForm.name" type="text" class="form-input"
                                        placeholder="Ex: Matinal" />
                                </div>
                                <div class="form-group">
                                    <label class="form-label">Entrée</label>
                                    <input v-model="groupForm.shift_start" type="time" class="form-input" />
                                </div>
                                <div class="form-group">
                                    <label class="form-label">Sortie</label>
                                    <input v-model="groupForm.shift_end" type="time" class="form-input" />
                                </div>
                                <div class="form-group">
                                    <label class="form-label">Tolérance retard (min)</label>
                                    <input v-model.number="groupForm.entry_tolerance_minutes" type="number" min="0" class="form-input" />
                                </div>
                                <button type="submit" class="icon-command primary" :disabled="groupLoading"
                                    :title="groupLoading ? 'Création...' : 'Créer le groupe horaire'">
                                    <svg v-if="!groupLoading" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2.5">
                                        <path d="M12 5v14M5 12h14" />
                                    </svg>
                                    <span v-else class="mini-spinner"></span>
                                </button>
                            </form>
                            <div v-if="groupSuccess" class="alert alert-success">{{ groupSuccess }}</div>
                            <div v-if="groupError" class="alert alert-error">{{ groupError }}</div>
                            <div class="posts-manager-list">
                                <div v-for="group in editableGroups" :key="group.originalName" class="post-config-row">
                                    <div class="post-config-main group-config-main">
                                        <label class="mini-field">
                                            <span>Groupe</span>
                                            <input v-model="group.name" type="text" class="form-input post-name-input" />
                                        </label>
                                        <label class="mini-field">
                                            <span>Entrée</span>
                                            <input v-model="group.shift_start" type="time" class="form-input" />
                                        </label>
                                        <label class="mini-field">
                                            <span>Sortie</span>
                                            <input v-model="group.shift_end" type="time" class="form-input" />
                                        </label>
                                        <label class="mini-field">
                                            <span>Tolérance</span>
                                            <input v-model.number="group.entry_tolerance_minutes" type="number" min="0" class="form-input" />
                                        </label>
                                    </div>
                                    <div class="row-command-group">
                                        <button type="button" class="icon-command"
                                            :disabled="group.saving"
                                            @click="updateGroup(group)"
                                            :title="group.saving ? 'Enregistrement...' : 'Enregistrer'">
                                            <svg v-if="!group.saving" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                stroke-width="2.5">
                                                <path d="M20 6 9 17l-5-5" />
                                            </svg>
                                            <span v-else class="mini-spinner"></span>
                                        </button>
                                        <button type="button" class="icon-command danger"
                                            :disabled="group.deleting"
                                            @click="deleteGroup(group)"
                                            :title="group.deleting ? 'Suppression...' : 'Supprimer'">
                                            <svg v-if="!group.deleting" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M3 6h18" />
                                                <path d="M8 6V4h8v2" />
                                                <path d="M19 6l-1 14H6L5 6" />
                                                <path d="M10 11v5M14 11v5" />
                                            </svg>
                                            <span v-else class="mini-spinner"></span>
                                        </button>
                                    </div>
                                </div>
                                <span v-if="workGroups.length === 0" class="empty-inline">Aucun groupe horaire créé</span>
                            </div>
                        </div>
                    </section>

                    <section class="profile-card">
                        <div class="card-header">
                            <div>
                                <p class="card-kicker">Données</p>
                                <h3>Sauvegarde</h3>
                            </div>
                        </div>

                        <div class="backup-body">
                            <p class="backup-help">Télécharger une copie complète de la base de données SQLite.</p>
                            <div class="form-actions backup-actions">
                                <button type="button" @click="downloadDatabaseBackup" :disabled="backupLoading"
                                    class="btn btn-primary">
                                    <span v-if="!backupLoading">Télécharger Backup (.db)</span>
                                    <span v-else>Téléchargement...</span>
                                </button>
                            </div>

                            <div v-if="backupSuccess" class="alert alert-success">
                                Backup téléchargé avec succès.
                            </div>
                            <div v-if="backupError" class="alert alert-error">
                                {{ backupError }}
                            </div>
                        </div>
                    </section>

                </aside>
            </div>

            <section v-if="isAdmin" class="profile-card users-card users-card-wide">
                <div class="card-header users-header">
                    <div>
                        <p class="card-kicker">Sécurité</p>
                        <h3>Utilisateurs</h3>
                    </div>
                    <div class="role-filter" aria-label="Filtrer les utilisateurs">
                        <button
                            v-for="filter in userRoleFilters"
                            :key="filter.value"
                            type="button"
                            :class="{ active: userRoleFilter === filter.value }"
                            @click="userRoleFilter = filter.value"
                        >
                            <span>{{ filter.label }}</span>
                            <b>{{ userCountByRole(filter.value) }}</b>
                        </button>
                    </div>
                </div>

                <div class="users-list">
                    <div v-if="filteredUsers.length === 0" class="empty-state">
                        <p>Aucun utilisateur trouvé</p>
                    </div>

                    <div v-for="user in filteredUsers" :key="user.id" class="user-item"
                        :class="{ 'is-current-user': isCurrentUser(user) }">
                        <div class="user-info">
                            <div class="user-title-row">
                                <h4>{{ user.username }}</h4>
                                <span v-if="isCurrentUser(user)" class="current-user-chip">Vous</span>
                                <span class="badge badge-admin">
                                    {{ roleLabel(user.privilage) }}
                                </span>
                                <span v-if="user.staff_name" class="current-user-chip">{{ user.staff_name }}</span>
                                <span v-if="user.cashier_name" class="current-user-chip">Caissier: {{ user.cashier_name }}</span>
                            </div>
                        </div>

                        <div class="user-actions">
                            <button v-if="!isCurrentUser(user)" @click="openEditUser(user)"
                                class="btn btn-small btn-info">
                                Modifier
                            </button>
                            <button v-if="!isCurrentUser(user)" @click="deleteUser(user.id, user.username)"
                                class="btn btn-small btn-danger">
                                Supprimer
                            </button>
                            <span v-else class="protected-note">Compte actuel</span>
                        </div>
                    </div>
                </div>
            </section>

        </div>

        <!-- Edit User Modal -->
        <div v-if="isAdmin && editFormVisible" class="modal-overlay" @click.self="editFormVisible = false">
            <div class="modal-content">
                <div class="modal-header">
                    <h2>Modifier {{ editingUser.username }}</h2>
                    <button class="close-btn" @click="editFormVisible = false" aria-label="Close">✕</button>
                </div>

                <form @submit.prevent="submitEditUserForm" class="form">
                    <div class="form-section">
                        <div class="form-row">
                            <div class="form-group">
                                <label for="editUsername" class="form-label">Nom d'utilisateur</label>
                                <input v-model="editForm.username" type="text" id="editUsername" class="form-input"
                                    required />
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="editPassword" class="form-label">Nouveau mot de passe (laisser vide pour garder
                                l'ancien)</label>
                            <input v-model="editForm.password" type="password" id="editPassword" class="form-input"
                                placeholder="••••••••" />
                        </div>
                        <div class="form-group">
                            <label for="editRole" class="form-label">Rôle</label>
                            <select v-model="editForm.privilage" id="editRole" class="form-input" :disabled="editingUser?.privilage === 'employe'">
                                <option value="admin">Admin</option>
                                <option value="caissier">Caissier</option>
                                <option value="employe">Employé</option>
                            </select>
                        </div>
                        <div v-if="editForm.privilage === 'employe'" class="form-group">
                            <label class="form-label">Employé lié</label>
                            <div class="readonly-field">{{ editForm.staff_name || 'Employé lié' }}</div>
                        </div>
                        <div v-if="editForm.privilage === 'employe'" class="form-group">
                            <label class="form-label">Caissier avances</label>
                            <select v-model.number="editForm.cashier_user_id" class="form-input">
                                <option :value="null">Aucun caissier</option>
                                <option v-for="user in cashierUsers" :key="user.id" :value="user.id">
                                    {{ user.username }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="form-actions">
                        <button type="button" @click="editFormVisible = false" class="btn btn-secondary">
                            Annuler
                        </button>
                        <button type="submit" :disabled="editUserLoading" class="btn btn-primary">
                            <span v-if="!editUserLoading">Mettre à jour</span>
                            <span v-else>Mise à jour...</span>
                        </button>
                    </div>

                    <div v-if="editUserSuccess" class="alert alert-success">
                        ✓ Utilisateur modifié avec succès!
                    </div>
                    <div v-if="editUserError" class="alert alert-error">
                        ✕ {{ editUserError }}
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AppProfile',
    inject: ['privilage'],
    data() {
        return {
            profileForm: {
                current_password: '',
                new_password: '',
                confirm_password: '',
            },
            profileLoading: false,
            profileSuccess: false,
            profileError: null,

            createUserForm: {
                username: '',
                password: '',
                passwordconf: '',
                role: 'caissier',
                staff_id: null,
                cashier_user_id: null,
            },
            createUserLoading: false,
            createUserSuccess: false,
            createUserError: null,

            bulkEmployeeForm: {
                cashier_user_id: null,
            },
            bulkEmployeeRows: [],
            bulkEmployeeLoading: false,
            bulkEmployeeSuccess: '',
            bulkEmployeeError: null,

            editForm: {},
            editingUser: null,
            editFormVisible: false,
            editUserLoading: false,
            editUserSuccess: false,
            editUserError: null,
            userRoleFilter: 'all',
            userRoleFilters: [
                { value: 'all', label: 'Tous' },
                { value: 'admin', label: 'Admin' },
                { value: 'caissier', label: 'Caissier' },
                { value: 'employe', label: 'Employé' },
            ],

            backupLoading: false,
            backupSuccess: false,
            backupError: null,

            settingsForm: {
                rh_closing_time: '05:00',
            },
            settingsLoading: false,
            settingsSuccess: false,
            settingsError: null,

            users: [],
            staffOptions: [],
            posts: [],
            editablePosts: [],
            workGroups: [],
            editableGroups: [],
            postForm: {
                name: '',
            },
            groupForm: {
                name: '',
                shift_start: '09:00',
                shift_end: '19:00',
                entry_tolerance_minutes: 15,
            },
            postLoading: false,
            postSuccess: '',
            postError: null,
            groupLoading: false,
            groupSuccess: '',
            groupError: null,
        };
    },
    mounted() {
        if (this.isAdmin) {
            this.fetchUsers();
            this.fetchPosts();
            this.fetchGroups();
            this.fetchSettings();
            this.fetchStaffOptions();
        }
    },
    computed: {
        isAdmin() {
            return typeof this.privilage === 'function' && this.privilage() === 'admin';
        },
        safeUsers() {
            return Array.isArray(this.users) ? this.users : [];
        },
        safePosts() {
            return Array.isArray(this.posts) ? this.posts : [];
        },
        cashierUsers() {
            return this.safeUsers.filter(user => user.privilage === 'caissier');
        },
        filteredUsers() {
            if (this.userRoleFilter === 'all') {
                return this.safeUsers;
            }
            return this.safeUsers.filter(user => user.privilage === this.userRoleFilter);
        },
        staffIDsWithAccounts() {
            return this.safeUsers
                .filter(user => user.privilage === 'employe' && user.staff_id)
                .map(user => Number(user.staff_id));
        },
        availableCreateStaff() {
            const used = new Set(this.staffIDsWithAccounts);
            return this.staffOptions.filter(staff => !used.has(Number(staff.id)));
        },
        selectedBulkRows() {
            return this.bulkEmployeeRows.filter(row => row.selected);
        },
        availableEditStaff() {
            const used = new Set(
                this.safeUsers
                    .filter(user => user.privilage === 'employe' && user.staff_id && user.id !== this.editForm.id)
                    .map(user => Number(user.staff_id))
            );
            return this.staffOptions.filter(staff => !used.has(Number(staff.id)));
        },
    },
    methods: {
        getCurrentUsername() {
            const cookie = document.cookie
                .split('; ')
                .find((row) => row.startsWith('session_id='));
            if (!cookie) {
                return '';
            }
            const value = decodeURIComponent(cookie.split('=')[1] || '');
            return value.includes('55') ? value.split('55').slice(1).join('55') : '';
        },

        isCurrentUser(user) {
            return user && user.username === this.getCurrentUsername();
        },

        roleLabel(role) {
            const labels = {
                admin: 'Admin',
                caissier: 'Caissier',
                employe: 'Employé',
            };
            return labels[role] || role;
        },

        userCountByRole(role) {
            if (role === 'all') {
                return this.safeUsers.length;
            }
            return this.safeUsers.filter(user => user.privilage === role).length;
        },

        staffLabel(staff) {
            if (!staff) return '';
            const name = `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || `Employé #${staff.id}`;
            return `${name} - ${staff.post_name || ''}`;
        },

        staffName(staff) {
            if (!staff) return '';
            return `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || `Employé #${staff.id}`;
        },

        isCreateEmployeeSelected(staffID) {
            return Number(this.createUserForm.staff_id) === Number(staffID);
        },

        selectCreateEmployee(staffID, checked) {
            this.createUserForm.staff_id = checked ? Number(staffID) : null;
        },

        isEditEmployeeSelected(staffID) {
            return Number(this.editForm.staff_id) === Number(staffID);
        },

        selectEditEmployee(staffID, checked) {
            this.editForm.staff_id = checked ? Number(staffID) : null;
        },

        suggestedUsername(staff) {
            const firstPart = String(staff?.first_name || '').trim().split(/\s+/)[0] || '';
            const raw = firstPart
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9]+/g, '');
            return `${raw || 'employe'}${staff?.id || ''}`;
        },

        prepareBulkRow(row) {
            if (row.selected && !row.username) {
                row.username = this.suggestedUsername(row.staff);
            }
        },

        syncBulkEmployeeRows() {
            const existing = new Map(this.bulkEmployeeRows.map(row => [Number(row.staff_id), row]));
            this.bulkEmployeeRows = this.availableCreateStaff.map((staff) => {
                const current = existing.get(Number(staff.id));
                return current || {
                    staff,
                    staff_id: Number(staff.id),
                    selected: false,
                    username: '',
                    password: '',
                    passwordconf: '',
                };
            });
        },

        openEditUser(user) {
            if (this.isCurrentUser(user)) {
                return;
            }
            this.editingUser = user;
            this.editForm = { ...user, password: '', cashier_user_id: user.cashier_user_id || null };
            this.editFormVisible = true;
        },

        async readErrorMessage(res, fallback) {
            const text = await res.text();
            if (!text) {
                return fallback;
            }
            try {
                const data = JSON.parse(text);
                return data.error || data.message || fallback;
            } catch (e) {
                return text.trim() || fallback;
            }
        },

        async submitProfileForm() {
            // Validation
            if (!this.profileForm.current_password || !this.profileForm.current_password.trim()) {
                this.profileError = 'Veuillez entrer votre mot de passe actuel';
                return;
            }

            if (!this.profileForm.new_password || !this.profileForm.new_password.trim()) {
                this.profileError = 'Veuillez entrer un nouveau mot de passe';
                return;
            }

            if (!this.profileForm.confirm_password || !this.profileForm.confirm_password.trim()) {
                this.profileError = 'Veuillez confirmer votre mot de passe';
                return;
            }

            if (this.profileForm.new_password.length < 6) {
                this.profileError = 'Le mot de passe doit contenir au moins 6 caractères';
                return;
            }

            if (this.profileForm.new_password !== this.profileForm.confirm_password) {
                this.profileError = 'Les mots de passe ne correspondent pas';
                return;
            }

            this.profileError = null;
            this.profileSuccess = false;
            this.profileLoading = true;

            try {
                const res = await fetch('/api-pointage/updateprofile', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(this.profileForm),
                });

                if (!res.ok) {
                    throw new Error(await this.readErrorMessage(res, 'Échec de la mise à jour'));
                }

                this.profileSuccess = true;
                this.resetProfileForm();
                setTimeout(() => {
                    this.profileSuccess = false;
                }, 3000);
            } catch (err) {
                this.profileError = err.message;
            } finally {
                this.profileLoading = false;
            }
        },

        resetProfileForm() {
            this.profileForm = {
                current_password: '',
                new_password: '',
                confirm_password: '',
            };
        },

        async submitCreateUserForm() {
            this.createUserError = null;
            this.createUserSuccess = false;
            if (this.createUserForm.role === 'employe') {
                if (!this.createUserForm.cashier_user_id) {
                    this.createUserError = 'Choisissez un caissier';
                    return;
                }
                if (!this.createUserForm.staff_id) {
                    this.createUserError = 'Cochez un employé';
                    return;
                }
            }
            this.createUserLoading = true;

            try {
                const res = await fetch('/api-pointage/createuser', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(this.createUserForm),
                });

                if (!res.ok) {
                    throw new Error(await this.readErrorMessage(res, 'Échec de la création'));
                }

                this.createUserSuccess = true;
                this.resetCreateUserForm();
                await this.fetchUsers();
                setTimeout(() => {
                    this.createUserSuccess = false;
                }, 3000);
            } catch (err) {
                this.createUserError = err.message;
            } finally {
                this.createUserLoading = false;
            }
        },

        resetCreateUserForm() {
                this.createUserForm = {
                    username: '',
                    password: '',
                    passwordconf: '',
                    role: 'caissier',
                    staff_id: null,
                    cashier_user_id: null,
                };
        },

        async fetchStaffOptions() {
            try {
                const res = await fetch('/api-pointage/staff-options', {
                    credentials: 'include',
                    cache: 'no-store',
                });
                if (!res.ok) throw new Error(await this.readErrorMessage(res, 'Échec du chargement des employés'));
                const data = await res.json();
                this.staffOptions = Array.isArray(data) ? data : [];
                this.syncBulkEmployeeRows();
            } catch (err) {
                this.staffOptions = [];
                this.bulkEmployeeRows = [];
            }
        },

        resetBulkEmployeeForm(clearMessage = true) {
            this.bulkEmployeeForm = { cashier_user_id: null };
            this.bulkEmployeeRows = this.availableCreateStaff.map((staff) => ({
                staff,
                staff_id: Number(staff.id),
                selected: false,
                username: '',
                password: '',
                passwordconf: '',
            }));
            if (clearMessage) {
                this.bulkEmployeeError = null;
                this.bulkEmployeeSuccess = '';
            }
        },

        validateBulkEmployeeUsers() {
            if (!this.bulkEmployeeForm.cashier_user_id) {
                return 'Choisissez un caissier';
            }
            const selected = this.selectedBulkRows;
            if (selected.length === 0) {
                return 'Cochez au moins un employé';
            }
            const usernames = new Set();
            for (const row of selected) {
                if (!row.username) {
                    return `Nom d'utilisateur requis pour ${this.staffName(row.staff)}`;
                }
                if (usernames.has(row.username)) {
                    return `Nom d'utilisateur dupliqué: ${row.username}`;
                }
                usernames.add(row.username);
                if (!row.password) {
                    return `Mot de passe requis pour ${this.staffName(row.staff)}`;
                }
                if (row.password !== row.passwordconf) {
                    return `Confirmation incorrecte pour ${this.staffName(row.staff)}`;
                }
            }
            return '';
        },

        async submitBulkEmployeeUsers() {
            this.bulkEmployeeError = null;
            this.bulkEmployeeSuccess = '';
            const validationError = this.validateBulkEmployeeUsers();
            if (validationError) {
                this.bulkEmployeeError = validationError;
                return;
            }
            this.bulkEmployeeLoading = true;
            try {
                const res = await fetch('/api-pointage/create-employee-users', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        cashier_user_id: this.bulkEmployeeForm.cashier_user_id,
                        employees: this.selectedBulkRows.map(row => ({
                            staff_id: row.staff_id,
                            username: row.username,
                            password: row.password,
                            passwordconf: row.passwordconf,
                        })),
                    }),
                });
                if (!res.ok) {
                    throw new Error(await this.readErrorMessage(res, 'Échec de la création des comptes employés'));
                }
                const data = await res.json();
                this.bulkEmployeeSuccess = `${data.created || this.selectedBulkRows.length} compte(s) employé(s) créé(s).`;
                await this.fetchUsers();
                await this.fetchStaffOptions();
                this.resetBulkEmployeeForm(false);
                setTimeout(() => {
                    this.bulkEmployeeSuccess = '';
                }, 3500);
            } catch (err) {
                this.bulkEmployeeError = err.message;
            } finally {
                this.bulkEmployeeLoading = false;
            }
        },

        async fetchSettings() {
            try {
                const res = await fetch('/api-pointage/settings', {
                    credentials: 'include',
                    cache: 'no-store',
                });
                if (!res.ok) throw new Error(await this.readErrorMessage(res, 'Échec du chargement des paramètres'));
                const data = await res.json();
                this.settingsForm.rh_closing_time = data.rh_closing_time || '05:00';
            } catch (err) {
                this.settingsError = err.message;
            }
        },

        async saveSettings() {
            this.settingsError = null;
            this.settingsSuccess = false;
            this.settingsLoading = true;
            try {
                const res = await fetch('/api-pointage/settings', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        rh_closing_time: this.settingsForm.rh_closing_time,
                        notification_missing_checkout: '1',
                        notification_auto_absence: '1',
                    }),
                });
                if (!res.ok) throw new Error(await this.readErrorMessage(res, 'Échec de la sauvegarde'));
                const data = await res.json();
                this.settingsForm.rh_closing_time = data.rh_closing_time || this.settingsForm.rh_closing_time;
                this.settingsSuccess = true;
                setTimeout(() => {
                    this.settingsSuccess = false;
                }, 3000);
            } catch (err) {
                this.settingsError = err.message;
            } finally {
                this.settingsLoading = false;
            }
        },

        async submitEditUserForm() {
            this.editUserError = null;
            this.editUserSuccess = false;
            this.editUserLoading = true;

            const payload = {
                id: this.editForm.id,
                username: this.editForm.username,
                role: this.editForm.privilage || 'admin',
                staff_id: this.editForm.privilage === 'employe' ? this.editForm.staff_id : null,
                cashier_user_id: this.editForm.privilage === 'employe' ? this.editForm.cashier_user_id : null,
            };

            if (payload.role === 'employe') {
                if (!payload.staff_id) {
                    this.editUserError = 'Compte employé sans employé lié';
                    this.editUserLoading = false;
                    return;
                }
            }

            if (this.editForm.password) {
                payload.password = this.editForm.password;
            }

            try {
                const res = await fetch('/api-pointage/updateuser', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    throw new Error(await this.readErrorMessage(res, 'Échec de la mise à jour'));
                }

                this.editUserSuccess = true;
                await this.fetchUsers();
                setTimeout(() => {
                    this.editFormVisible = false;
                    this.editUserSuccess = false;
                }, 1000);
            } catch (err) {
                this.editUserError = err.message;
            } finally {
                this.editUserLoading = false;
            }
        },

        async deleteUser(id, username) {
            const user = this.users.find((item) => item.id === id);
            if (this.isCurrentUser(user)) {
                alert('Vous ne pouvez pas supprimer votre propre compte.');
                return;
            }
            if (!confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${username} ?`)) {
                return;
            }

            try {
                const res = await fetch(`/api-pointage/deleteuser/${id}`, {
                    method: 'DELETE',
                });

                if (!res.ok) {
                    throw new Error(await this.readErrorMessage(res, 'Échec de la suppression'));
                }

                await this.fetchUsers();
            } catch (err) {
                alert(`Erreur: ${err.message}`);
            }
        },

        async fetchUsers() {
            try {
                const res = await fetch('/api-pointage/fetchusers');
                if (!res.ok) throw new Error('Échec du chargement des utilisateurs');
                const data = await res.json();
                this.users = Array.isArray(data) ? data : [];
                this.syncBulkEmployeeRows();

            } catch (err) {
                console.error('Erreur:', err);
            }
        },

        async fetchPosts() {
            try {
                const res = await fetch('/api-pointage/fetchposts', {
                    credentials: 'include',
                    cache: 'no-store',
                });
                if (!res.ok) throw new Error('Échec du chargement des postes');
                const data = await res.json();
                this.posts = Array.isArray(data) ? data : [];
                this.editablePosts = this.posts.map((post) => ({
                    originalName: post.Post_name,
                    name: post.Post_name,
                    saving: false,
                    deleting: false,
                }));
            } catch (err) {
                console.error('Erreur postes:', err);
                this.posts = [];
                this.editablePosts = [];
            }
        },

        async fetchGroups() {
            try {
                const res = await fetch('/api-pointage/fetchgroups', {
                    credentials: 'include',
                    cache: 'no-store',
                });
                if (!res.ok) throw new Error('Échec du chargement des groupes horaires');
                const data = await res.json();
                this.workGroups = Array.isArray(data) ? data : [];
                this.editableGroups = this.workGroups.map((group) => ({
                    originalName: group.group_name,
                    name: group.group_name,
                    shift_start: group.shift_start || '09:00',
                    shift_end: group.shift_end || '19:00',
                    entry_tolerance_minutes: group.entry_tolerance_minutes || 15,
                    saving: false,
                    deleting: false,
                }));
            } catch (err) {
                console.error('Erreur groupes horaires:', err);
                this.workGroups = [];
                this.editableGroups = [];
            }
        },

        async addPost() {
            this.postError = null;
            this.postSuccess = '';
            const name = this.postForm.name.trim();
            if (!name) {
                this.postError = 'Nom du poste requis';
                return;
            }
            this.postLoading = true;
            try {
                const params = new URLSearchParams({ name });
                const res = await fetch(`/api-pointage/addpost?${params.toString()}`, {
                    method: 'POST',
                    credentials: 'include',
                });
                if (!res.ok) throw new Error(await this.readErrorMessage(res, 'Échec de la création du poste'));
                const createdPost = { Post_name: name };
                this.posts = [...this.safePosts.filter((post) => post.Post_name !== name), createdPost];
                this.postSuccess = 'Poste créé avec succès.';
                this.postForm = { name: '' };
                await this.fetchPosts();
                window.dispatchEvent(new CustomEvent('posts-updated'));
                setTimeout(() => { this.postSuccess = ''; }, 2500);
            } catch (err) {
                this.postError = err.message;
            } finally {
                this.postLoading = false;
            }
        },

        async updatePost(post) {
            this.postError = null;
            this.postSuccess = '';
            const newName = post.name.trim();
            if (!newName) {
                this.postError = 'Nom du poste requis';
                return;
            }
            post.saving = true;
            try {
                const res = await fetch('/api-pointage/updatepost', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        old_name: post.originalName,
                        new_name: newName,
                    }),
                });
                if (!res.ok) throw new Error(await this.readErrorMessage(res, 'Échec de la modification du poste'));
                this.postSuccess = 'Poste modifié avec succès.';
                await this.fetchPosts();
                window.dispatchEvent(new CustomEvent('posts-updated'));
                setTimeout(() => { this.postSuccess = ''; }, 2500);
            } catch (err) {
                this.postError = err.message;
            } finally {
                post.saving = false;
            }
        },

        async deletePost(post) {
            this.postError = null;
            this.postSuccess = '';
            if (!confirm(`Supprimer le poste ${post.originalName} ?`)) {
                return;
            }
            post.deleting = true;
            try {
                const res = await fetch(`/api-pointage/deletepost?post=${encodeURIComponent(post.originalName)}`, {
                    method: 'DELETE',
                    credentials: 'include',
                });
                if (!res.ok) throw new Error(await this.readErrorMessage(res, 'Échec de la suppression du poste'));
                this.postSuccess = 'Poste supprimé avec succès.';
                await this.fetchPosts();
                window.dispatchEvent(new CustomEvent('posts-updated'));
                setTimeout(() => { this.postSuccess = ''; }, 2500);
            } catch (err) {
                this.postError = err.message;
            } finally {
                post.deleting = false;
            }
        },

        async addGroup() {
            this.groupError = null;
            this.groupSuccess = '';
            const name = this.groupForm.name.trim();
            if (!name) {
                this.groupError = 'Nom du groupe horaire requis';
                return;
            }
            this.groupLoading = true;
            try {
                const params = new URLSearchParams({
                    name,
                    shift_start: this.groupForm.shift_start || '09:00',
                    shift_end: this.groupForm.shift_end || '19:00',
                    entry_tolerance_minutes: String(this.groupForm.entry_tolerance_minutes || 15),
                });
                const res = await fetch(`/api-pointage/addgroup?${params.toString()}`, {
                    method: 'POST',
                    credentials: 'include',
                });
                if (!res.ok) throw new Error(await this.readErrorMessage(res, 'Échec de la création du groupe horaire'));
                this.groupSuccess = 'Groupe horaire créé avec succès.';
                this.groupForm = { name: '', shift_start: '09:00', shift_end: '19:00', entry_tolerance_minutes: 15 };
                await this.fetchGroups();
                setTimeout(() => { this.groupSuccess = ''; }, 2500);
            } catch (err) {
                this.groupError = err.message;
            } finally {
                this.groupLoading = false;
            }
        },

        async updateGroup(group) {
            this.groupError = null;
            this.groupSuccess = '';
            const newName = group.name.trim();
            if (!newName) {
                this.groupError = 'Nom du groupe horaire requis';
                return;
            }
            group.saving = true;
            try {
                const res = await fetch('/api-pointage/updategroup', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        old_name: group.originalName,
                        new_name: newName,
                        shift_start: group.shift_start || '09:00',
                        shift_end: group.shift_end || '19:00',
                        entry_tolerance_minutes: Number(group.entry_tolerance_minutes || 15),
                    }),
                });
                if (!res.ok) throw new Error(await this.readErrorMessage(res, 'Échec de la modification du groupe horaire'));
                this.groupSuccess = 'Groupe horaire modifié avec succès.';
                await this.fetchGroups();
                setTimeout(() => { this.groupSuccess = ''; }, 2500);
            } catch (err) {
                this.groupError = err.message;
            } finally {
                group.saving = false;
            }
        },

        async deleteGroup(group) {
            this.groupError = null;
            this.groupSuccess = '';
            if (!confirm(`Supprimer le groupe horaire ${group.originalName} ?`)) {
                return;
            }
            group.deleting = true;
            try {
                const res = await fetch(`/api-pointage/deletegroup?name=${encodeURIComponent(group.originalName)}`, {
                    method: 'DELETE',
                    credentials: 'include',
                });
                if (!res.ok) throw new Error(await this.readErrorMessage(res, 'Échec de la suppression du groupe horaire'));
                this.groupSuccess = 'Groupe horaire supprimé avec succès.';
                await this.fetchGroups();
                setTimeout(() => { this.groupSuccess = ''; }, 2500);
            } catch (err) {
                this.groupError = err.message;
            } finally {
                group.deleting = false;
            }
        },

        async downloadDatabaseBackup() {
            this.backupError = null;
            this.backupSuccess = false;
            this.backupLoading = true;

            try {
                const res = await fetch('/api-pointage/backup-db', {
                    method: 'GET',
                });

                if (!res.ok) {
                    const errorText = await res.text();
                    throw new Error(errorText || 'Échec du téléchargement de la sauvegarde');
                }

                const blob = await res.blob();
                const contentDisposition = res.headers.get('content-disposition') || '';
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
                const fallbackDate = new Date().toISOString().slice(0, 10);
                const fileName = filenameMatch ? filenameMatch[1] : `pointage-backup-${fallbackDate}.db`;

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                this.backupSuccess = true;
                setTimeout(() => {
                    this.backupSuccess = false;
                }, 3000);
            } catch (err) {
                this.backupError = err.message;
            } finally {
                this.backupLoading = false;
            }
        },
    },
};
</script>

<style scoped>
.profile-wrapper {
    padding: 0;
    min-height: 100vh;
}

.profile-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 22px 20px;
}

.profile-card {
    background: var(--white);
    border: 1px solid var(--gray-200);
    border-radius: 8px;
    margin-bottom: 14px;
    overflow: hidden;
}

.card-header {
    padding: 15px 18px;
    border-bottom: 1px solid var(--gray-200);
    background: var(--gray-50);
}

.card-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--gray-900);
}

.form {
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.form-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.form-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--gray-700);
}

.form-help {
    margin: 0;
    color: var(--gray-500);
    font-size: 12px;
    line-height: 1.5;
}

.form-input {
    padding: 10px 12px;
    border: 1px solid var(--gray-300);
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
}

.form-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
}

.backup-body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.backup-help {
    margin: 0;
    color: var(--gray-600);
    font-size: 14px;
}

.backup-actions {
    justify-content: flex-start;
}

.alert {
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
}

.alert-success {
    background: #f0fdf4;
    color: #166534;
    border: 1px solid #bbf7d0;
}

.alert-error {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
}

/* Users List */
.users-list {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.empty-state {
    text-align: center;
    padding: 40px 20px;
    color: var(--gray-500);
}

.user-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    background: var(--gray-50);
    border-radius: 8px;
    border: 1px solid var(--gray-200);
}

.user-info {
    flex: 1;
}

.user-info h4 {
    margin: 0 0 4px 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--gray-900);
}

.user-info p {
    margin: 0 0 8px 0;
    font-size: 13px;
    color: var(--gray-600);
}

.badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
}

.badge-admin {
    background: rgba(102, 126, 234, 0.1);
    color: var(--primary);
}

.badge-user {
    background: rgba(107, 114, 128, 0.1);
    color: var(--gray-700);
}

.user-actions {
    display: flex;
    gap: 8px;
}

.btn-small {
    padding: 6px 12px;
    font-size: 12px;
}

/* Modal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.modal-content {
    background: var(--white);
    border-radius: 12px;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid var(--gray-200);
}

.modal-header h2 {
    margin: 0;
    font-size: 20px;
}

.close-btn {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 6px;
    background: var(--gray-100);
    cursor: pointer;
    font-size: 18px;
    transition: all 0.2s ease;
}

.close-btn:hover {
    background: var(--gray-200);
}

@media (max-width: 768px) {
    .profile-container {
        padding: 16px;
    }

    .form-row {
        grid-template-columns: 1fr;
    }

    .user-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .user-actions {
        width: 100%;
    }

    .user-actions .btn {
        flex: 1;
    }

    .form-actions {
        flex-direction: column;
    }

    .form-actions .btn {
        width: 100%;
    }
}

row:last-child { border-bottom: none; }
.user-row:hover { background: var(--gray-50); }
.user-avatar { width: 36px; height: 36px; background: var(--primary-glow); border-radius: 50%; display: flex;
align-items: center; justify-content: center; font-size: 13px; font-weight: 800; color: var(--primary); flex-shrink: 0;
text-transform: uppercase; }
.user-details { flex: 1; min-width: 0; }
.user-name { font-size: 13.5px; font-weight: 600; color: var(--gray-800); }
.user-email { font-size: 12px; color: var(--gray-500); margin-top: 1px; }
.priv-badge { display: inline-flex; padding: 3px 10px; border-radius: var(--radius-full); font-size: 11px; font-weight:
700; flex-shrink: 0; }
.priv-admin { background: var(--primary-glow); color: var(--primary); }
.priv-user { background: var(--gray-100); color: var(--gray-600); }
.user-actions { display: flex; gap: 6px; flex-shrink: 0; }
.user-actions .btn svg { width: 13px; height: 13px; }
.list-empty { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 32px; color:
var(--gray-400); font-size: 13px; }
.list-empty svg { width: 28px; height: 28px; }

@media (max-width: 768px) { .profile-grid { grid-template-columns: 1fr; } }

.profile-wrapper {
    background: #f6f8fc;
}

.profile-container {
    max-width: 1060px;
    padding: 24px 20px 44px;
}

.profile-page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
}

.profile-eyebrow {
    margin: 0 0 4px;
    color: var(--primary);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
}

.profile-page-header h1 {
    margin: 0;
    color: var(--gray-900);
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0;
}

.settings-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 340px;
    gap: 18px;
    align-items: start;
}

.settings-main,
.settings-side {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.profile-card {
    margin-bottom: 0;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.04);
}

.card-header {
    padding: 15px 20px;
    background: #fff;
}

.card-header h3 {
    font-size: 15px;
    font-weight: 700;
}

.form,
.backup-body,
.users-list {
    padding: 20px;
}

.form-label {
    font-size: 11px;
    font-weight: 700;
    color: #44546f;
    text-transform: uppercase;
    letter-spacing: 0;
}

.form-input {
    min-height: 40px;
    border-color: #d6deef;
    background: #fbfcff;
}

.form-input:focus {
    background: #fff;
}

.btn {
    min-height: 34px;
    border-radius: 8px;
}

.users-card-wide {
    margin-top: 18px;
}

.user-item {
    align-items: center;
    gap: 14px;
    background: #fff;
    border-color: #dce4f2;
    box-shadow: none;
}

.user-item.is-current-user {
    border-color: rgba(37, 99, 235, 0.35);
    background: #f7faff;
}

.user-title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 0;
}

.user-info h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
}

.current-user-chip,
.protected-note {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 4px 9px;
    border-radius: 999px;
    background: #e8f0ff;
    color: #1d4ed8;
    font-size: 11px;
    font-weight: 700;
}

.protected-note {
    background: #eef2f7;
    color: #526070;
}

.badge {
    border-radius: 999px;
}

.user-actions {
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
}

.backup-actions {
    justify-content: stretch;
}

.backup-actions .btn {
    width: 100%;
}

@media (max-width: 1100px) {
    .settings-grid {
        grid-template-columns: 1fr;
    }

}

@media (max-width: 768px) {
    .profile-container {
        padding: 16px 12px 32px;
    }

    .profile-page-header h1 {
        font-size: 20px;
    }

    .settings-side {
        display: flex;
    }

    .card-header,
    .form,
    .backup-body,
    .users-list {
        padding: 16px;
    }

    .user-item {
        align-items: stretch;
    }

    .user-actions {
        justify-content: stretch;
    }
}

.settings-overview {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 18px;
}

.overview-tile {
    min-height: 74px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #dce4f2;
    border-radius: 10px;
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.04);
}

.overview-label {
    display: block;
    color: #526070;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0;
}

.overview-tile strong {
    display: block;
    margin-top: 6px;
    color: var(--gray-900);
    font-size: 24px;
    line-height: 1;
}

.organization-card {
    overflow: visible;
}

.card-header-split {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
}

.card-kicker {
    margin: 0 0 3px;
    color: var(--primary);
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
}

.organization-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    padding: 18px;
}

.manager-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background: #f8fbff;
    border: 1px solid #dce4f2;
    border-radius: 10px;
}

.manager-title h4 {
    margin: 0;
    font-size: 14px;
    color: var(--gray-900);
}

.manager-form {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.time-controls {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.chips-list,
.groups-manager-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.entity-chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 30px;
    padding: 6px 10px;
    border: 1px solid #dce4f2;
    border-radius: 999px;
    background: #fff;
    color: #24324a;
    font-size: 12px;
    font-weight: 700;
}

.entity-chip i {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex: 0 0 auto;
}

.empty-inline {
    color: var(--gray-500);
    font-size: 12px;
    font-weight: 600;
    padding: 7px 0;
}

.group-manager-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto 28px;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px;
    background: #fff;
    border: 1px solid #dce4f2;
    border-radius: 8px;
}

.group-manager-row small {
    color: #526070;
    font-weight: 700;
    white-space: nowrap;
}

.icon-danger-btn {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 7px;
    background: var(--danger-bg);
    color: var(--danger);
    font-size: 18px;
    font-weight: 800;
    cursor: pointer;
}

@media (max-width: 900px) {
    .organization-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 640px) {
    .settings-overview {
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
    }

    .overview-tile {
        min-height: 64px;
        padding: 11px;
        border-radius: 9px;
    }

    .overview-tile strong {
        font-size: 20px;
    }

    .organization-grid {
        padding: 14px;
    }

    .manager-panel {
        padding: 14px;
    }

    .group-manager-row {
        grid-template-columns: 1fr 28px;
    }

    .group-manager-row small {
        grid-column: 1;
    }
}

/* Final settings layout */
.profile-wrapper {
    min-height: 100vh;
    background: #f5f7fb;
}

.profile-container {
    max-width: 1180px;
    padding: 28px 28px 42px;
}

.profile-page-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    gap: 20px;
    margin-bottom: 18px;
}

.profile-page-header h1 {
    font-size: 24px;
}

.settings-overview {
    display: grid;
    grid-template-columns: repeat(2, 132px);
    gap: 10px;
    margin: 0;
}

.overview-tile {
    min-height: 64px;
    padding: 12px 14px;
    border-radius: 8px;
    background: #fff;
}

.overview-tile strong {
    font-size: 22px;
}

.settings-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 340px;
    gap: 18px;
}

.settings-main,
.settings-side {
    gap: 18px;
}

.profile-card {
    border: 1px solid #dbe3f1;
    border-radius: 8px;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.045);
}

.card-header {
    min-height: 58px;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    background: #fff;
}

.card-header h3 {
    font-size: 16px;
}

.card-kicker {
    margin: 0 0 2px;
    font-size: 11px;
    color: #2563eb;
}

.form,
.backup-body,
.organization-body,
.users-list {
    padding: 18px 20px;
}

.form {
    gap: 16px;
}

.form-section {
    gap: 14px;
}

.form-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.form-row-three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.form-label {
    font-size: 10.5px;
    color: #42526e;
}

.form-input {
    min-height: 38px;
    border-radius: 7px;
}

.form-actions {
    padding-top: 2px;
}

.btn {
    min-height: 36px;
    padding: 0 18px;
    border-radius: 7px;
}

.organization-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.manager-form {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 40px;
    align-items: end;
    gap: 10px;
}

.post-create-form {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
}

.posts-manager-list {
    max-height: 280px;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-right: 4px;
}

.post-config-row {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 8px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
}

.post-config-main {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    align-items: center;
}

.post-name-input {
    font-weight: 700;
}

.switch-line {
    min-height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0 8px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: var(--gray-700);
    background: #f8fafc;
    font-size: 12px;
    font-weight: 800;
}

.switch-line.compact {
    min-height: 36px;
}

.switch-line input {
    min-height: auto;
}

.row-command-group {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.post-edit-field {
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr);
    align-items: center;
    min-width: 0;
    border: 1px solid #dbe3f1;
    border-radius: 7px;
    background: #f8fbff;
    overflow: hidden;
}

.post-edit-field .form-input {
    min-height: 36px;
    border: 0;
    background: transparent;
    box-shadow: none;
}

.post-edit-field .form-input:focus {
    box-shadow: none;
}

.post-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    color: #64748b;
}

.post-icon svg,
.icon-command svg {
    width: 16px;
    height: 16px;
}

.icon-command {
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #dbe3f1;
    border-radius: 7px;
    background: #f8fbff;
    color: #334155;
    cursor: pointer;
    transition: all 0.18s ease;
}

.icon-command:hover:not(:disabled) {
    border-color: #2563eb;
    background: #eff6ff;
    color: #1d4ed8;
}

.icon-command.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
}

.icon-command.primary:hover:not(:disabled) {
    background: #1d4ed8;
    color: #fff;
}

.icon-command.danger {
    color: #b91c1c;
}

.icon-command.danger:hover:not(:disabled) {
    border-color: #fecaca;
    background: #fef2f2;
    color: #dc2626;
}

.icon-command:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.mini-spinner {
    width: 15px;
    height: 15px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 999px;
    animation: spinMini 0.75s linear infinite;
}

@keyframes spinMini {
    to {
        transform: rotate(360deg);
    }
}

.backup-body {
    gap: 14px;
}

.backup-help {
    line-height: 1.55;
}

.users-card-wide {
    margin-top: 18px;
}

.users-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
}

.role-filter {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    border: 1px solid var(--gray-200);
    border-radius: 8px;
    background: #fff;
}

.role-filter button {
    min-height: 32px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 6px 10px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--gray-600);
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
}

.role-filter button.active {
    background: var(--primary);
    color: #fff;
}

.role-filter b {
    min-width: 20px;
    padding: 2px 6px;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.08);
    text-align: center;
    font-size: 11px;
}

.role-filter button.active b {
    background: rgba(255, 255, 255, 0.22);
}

.users-list {
    gap: 10px;
}

.assignment-panel {
    border: 1px solid var(--gray-200);
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
}

.assignment-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    border-bottom: 1px solid var(--gray-200);
    background: var(--gray-50);
}

.assignment-toolbar strong {
    color: var(--gray-900);
    font-size: 14px;
}

.assignment-toolbar span {
    color: var(--gray-500);
    font-size: 13px;
}

.assignment-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-height: 340px;
    overflow: auto;
}

.assignment-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
    padding: 12px 14px;
    border-bottom: 1px solid var(--gray-100);
    cursor: pointer;
}

.assignment-row:nth-child(odd) {
    border-right: 1px solid var(--gray-100);
}

.assignment-row input {
    margin-top: 3px;
}

.assignment-row span {
    display: grid;
    gap: 3px;
    min-width: 0;
}

.assignment-row strong,
.assignment-row small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.assignment-row strong {
    color: var(--gray-900);
    font-size: 14px;
}

.assignment-row small {
    color: var(--gray-500);
    font-size: 12px;
}

.bulk-summary {
    min-height: 42px;
    align-self: end;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid var(--gray-200);
    border-radius: 8px;
    background: var(--gray-50);
}

.bulk-summary strong {
    color: var(--primary);
    font-size: 18px;
}

.bulk-summary span {
    color: var(--gray-600);
    font-size: 13px;
}

.bulk-panel {
    border: 1px solid var(--gray-200);
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
}

.bulk-row {
    padding: 0;
    border-bottom: 1px solid var(--gray-100);
}

.bulk-row:last-child {
    border-bottom: 0;
}

.bulk-row.selected {
    background: #f8fbff;
}

.bulk-employee-line {
    display: flex;
    align-items: center;
    gap: 11px;
    min-width: 0;
    padding: 12px 14px;
    cursor: pointer;
}

.bulk-employee-line input {
    flex: 0 0 auto;
}

.bulk-employee-line span {
    display: grid;
    gap: 3px;
    flex: 1;
    min-width: 0;
}

.bulk-employee-line strong,
.bulk-employee-line small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bulk-employee-line strong {
    color: var(--gray-900);
    font-size: 14px;
}

.bulk-employee-line small {
    color: var(--gray-500);
    font-size: 12px;
}

.bulk-employee-line b {
    flex: 0 0 auto;
    padding: 4px 8px;
    border-radius: 999px;
    background: rgba(37, 99, 235, 0.1);
    color: var(--primary);
    font-size: 11px;
    font-weight: 800;
}

.bulk-fields {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
    padding: 0 14px 14px 39px;
}

.user-item {
    min-height: 58px;
    padding: 12px 14px;
    border-radius: 8px;
}

.badge,
.current-user-chip,
.protected-note {
    min-height: 22px;
    padding: 3px 9px;
}

@media (max-width: 1180px) {
    .profile-container {
        padding: 22px 18px 36px;
    }

    .settings-grid {
        grid-template-columns: 1fr;
    }

    .settings-side {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 760px) {
    .profile-page-header {
        grid-template-columns: 1fr;
        align-items: start;
    }

    .settings-overview,
    .settings-side,
    .form-row,
    .form-row-three {
        grid-template-columns: 1fr;
    }

    .overview-tile {
        min-height: 58px;
    }

    .card-header,
    .form,
    .backup-body,
    .organization-body,
    .users-list {
        padding: 15px;
    }

    .post-config-main {
        grid-template-columns: 1fr;
    }

    .row-command-group {
        justify-content: stretch;
    }

    .row-command-group .icon-command {
        flex: 1;
    }

    .manager-form {
        grid-template-columns: minmax(0, 1fr) 40px;
    }

    .assignment-list {
        grid-template-columns: 1fr;
    }

    .assignment-row:nth-child(odd) {
        border-right: 0;
    }

    .bulk-employee-line {
        align-items: flex-start;
    }

    .bulk-employee-line b {
        display: none;
    }

    .bulk-fields {
        padding-left: 14px;
    }
}

/* Profile polish overrides */
.profile-container {
    max-width: 1240px;
}

.settings-grid {
    grid-template-columns: minmax(0, 1fr) minmax(420px, 460px);
}

.settings-side {
    min-width: 0;
}

.form-row-three {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.group-create-form {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
}

.group-create-form .icon-command {
    width: 100%;
}

.group-config-main {
    grid-template-columns: 1fr 1fr;
    align-items: end;
}

.mini-field {
    display: grid;
    gap: 5px;
    min-width: 0;
}

.mini-field span {
    color: #526070;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
}

.mini-field .form-input {
    width: 100%;
}

.organization-card .posts-manager-list {
    max-height: 360px;
}

.users-card-wide {
    max-width: none;
}

@media (max-width: 1180px) {
    .settings-grid {
        grid-template-columns: 1fr;
    }

    .settings-side {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .organization-card:last-of-type {
        grid-column: 1 / -1;
    }
}

@media (max-width: 900px) {
    .settings-side {
        grid-template-columns: 1fr;
    }

    .group-config-main {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 640px) {
    .profile-container {
        padding: 14px 10px 32px;
    }

    .profile-page-header {
        gap: 12px;
        margin-bottom: 14px;
    }

    .settings-overview {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .profile-card {
        border-radius: 10px;
    }

    .form-row-three,
    .group-create-form,
    .group-config-main,
    .post-config-main {
        grid-template-columns: 1fr;
    }

    .post-config-row {
        padding: 12px;
    }

    .row-command-group {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .row-command-group .icon-command {
        width: 100%;
    }

    .user-item {
        min-height: 0;
    }

    .users-header {
        align-items: stretch;
        flex-direction: column;
    }

    .role-filter {
        width: 100%;
        overflow-x: auto;
    }

    .role-filter button {
        flex: 1 0 auto;
        justify-content: center;
    }
}
</style>
